import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Avatar,
  Menu,
  MenuItem,
  Divider as MuiDivider,
  Tabs,
  Tab,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search,
  Settings as SettingsIcon,
  Logout,
  CloudUpload,
  Menu as MenuIcon,
} from '@mui/icons-material';
import Lottie from 'lottie-react';
import SearchPage from './components/SearchPage';
import TransactionDetail from './components/TransactionDetail';
import EntityDetail from './components/EntityDetail';
import ImportPage from './components/ImportPage';
import SettingsPage from './components/SettingsPage';
import Login from './components/Login';
import AccessDenied from './components/common/AccessDenied';
import Breadcrumbs from './components/common/Breadcrumbs';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import animatedLogo from './assets/logos/IMPRES-Logo-Animated-Black.json';
import './App.css';

function AppContent() {
  const { isAuthenticated, isLoading, currentUser, accessDenied, clearAccessDenied, signOut, getCurrentUserPhoto, isAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);

  useEffect(() => {
    const fetchUserPhoto = async () => {
      if (isAuthenticated && currentUser) {
        const photo = await getCurrentUserPhoto();
        if (photo) setUserPhoto(photo);
      }
    };
    fetchUserPhoto();
  }, [isAuthenticated, currentUser, getCurrentUserPhoto]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    await signOut();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return accessDenied ? <AccessDenied onBack={clearAccessDenied} /> : <Login />;
  }

  return (
    <Router>
      <BreadcrumbProvider>
        <NavBar
          currentUser={currentUser}
          anchorEl={anchorEl}
          handleMenuOpen={handleMenuOpen}
          handleMenuClose={handleMenuClose}
          handleLogout={handleLogout}
          userPhoto={userPhoto}
          isAdmin={isAdmin}
        />
        <div className="App">
          <Breadcrumbs />
          <Box component="main" className="main-content">
            <Routes>
              <Route path="/" element={<SearchPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/transaction/:id" element={<TransactionDetail />} />
              <Route path="/entity/:entityId" element={<EntityDetail />} />
              {isAdmin() && (
                <>
                  <Route path="/import" element={<ImportPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </>
              )}
            </Routes>
          </Box>
        </div>
      </BreadcrumbProvider>
    </Router>
  );
}

// Mobile: hamburger + Drawer on xs; Tabs on sm+
function NavBar({ currentUser, anchorEl, handleMenuOpen, handleMenuClose, handleLogout, userPhoto, isAdmin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/' || path === '/search') return '/';
    if (path.startsWith('/transaction/')) return '/';
    if (path.startsWith('/entity/')) return '/';
    if (path === '/import') return '/import';
    if (path === '/settings') return '/settings';
    return '/';
  };

  const navItems = [
    { label: 'Search', to: '/', icon: <Search fontSize="small" /> },
    ...(isAdmin() ? [
      { label: 'Import', to: '/import', icon: <CloudUpload fontSize="small" /> },
      { label: 'Settings', to: '/settings', icon: <SettingsIcon fontSize="small" /> },
    ] : []),
  ];

  return (
    <nav className="nav-bar" style={{ paddingLeft: isMobile ? 8 : undefined, paddingRight: isMobile ? 8 : undefined }}>
      <div className="nav-bar-left" style={isMobile ? { gap: 2, padding: '0 2px' } : undefined}>
        {isMobile && (
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(true)}
            aria-label="open navigation"
            size="small"
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box
          component={Link}
          to="/"
          className="nav-bar-logo"
          sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
        >
          <Lottie
            animationData={animatedLogo}
            loop={true}
            style={{ height: isMobile ? 30 : 40, width: 'auto' }}
          />
        </Box>

        {isMobile && (
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#32578b', whiteSpace: 'nowrap', ml: 0.5 }}>
            QB Archive
          </Typography>
        )}

        {!isMobile && (
          <div className="nav-bar-tabs">
            <Tabs
              value={getActiveTab()}
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#32578b' },
                '& .MuiTab-root': {
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontWeight: 500,
                  fontSize: '14px',
                  minHeight: '48px',
                  '&.Mui-selected': { color: '#32578b' },
                },
              }}
            >
              <Tab
                label="Search"
                value="/"
                component={Link}
                to="/"
                icon={<Search fontSize="small" />}
                iconPosition="start"
              />
              {isAdmin() && (
                <Tab
                  label="Import"
                  value="/import"
                  component={Link}
                  to="/import"
                  icon={<CloudUpload fontSize="small" />}
                  iconPosition="start"
                />
              )}
              {isAdmin() && (
                <Tab
                  label="Settings"
                  value="/settings"
                  component={Link}
                  to="/settings"
                  icon={<SettingsIcon fontSize="small" />}
                  iconPosition="start"
                />
              )}
            </Tabs>
          </div>
        )}
      </div>

      {/* Mobile navigation drawer (hamburger) */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 240 }} role="presentation">
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center' }}>
            <Lottie animationData={animatedLogo} loop={true} style={{ height: 36, width: 'auto' }} />
          </Box>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  selected={getActiveTab() === item.to}
                  onClick={() => {
                    setDrawerOpen(false);
                    navigate(item.to);
                  }}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(50, 87, 139, 0.1)',
                      '& .MuiListItemIcon-root': { color: '#32578b' },
                      '& .MuiListItemText-primary': { color: '#32578b', fontWeight: 600 },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <div className="nav-bar-right">
        <IconButton onClick={handleMenuOpen}>
          <Avatar
            src={userPhoto}
            sx={{ width: 36, height: 36, bgcolor: '#32578b', fontSize: '0.9rem' }}
          >
            {currentUser?.firstName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {currentUser?.displayName || currentUser?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {currentUser?.email}
            </Typography>
            {currentUser?.roles?.length > 0 && (
              <Chip
                label={currentUser.roles.includes('Admin') ? 'Admin' : 'User'}
                size="small"
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: currentUser.roles.includes('Admin') ? 'rgba(50, 87, 139, 0.12)' : 'rgba(0,0,0,0.08)',
                  color: currentUser.roles.includes('Admin') ? '#32578b' : 'text.secondary',
                  fontWeight: 600,
                }}
              />
            )}
            <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 1 }}>
              v{process.env.REACT_APP_VERSION || '1.1.1'}
              {process.env.REACT_APP_GIT_HASH && process.env.REACT_APP_GIT_HASH !== 'unknown'
                ? ` (${process.env.REACT_APP_GIT_HASH})`
                : ''}
            </Typography>
          </Box>
          <MuiDivider />
          <MenuItem onClick={handleLogout}>
            <Logout fontSize="small" sx={{ mr: 1 }} />
            Sign Out
          </MenuItem>
        </Menu>
      </div>
    </nav>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
